import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnDestroy, OnInit, output } from '@angular/core';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { ButtonsOptions, HorizontalAlignmentOptions, VerticalAlignmentOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { AbstractCmsBlockComponent } from '../../../../../cms/block/abstract-cms-block.component';
import { ActionComponent } from '../../../../../ui/api/action.component';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';

export interface IProductBannerBlockDataContent extends NICmsContentComponent.IData {
    variant: string;
    image?: string;
    video?: string;
    video_id?: string;
    color: string;
    horizontal_align: string;
    vertical_align: string;
    title?: string;
    cta_text?: string;
    cta_url?: string;
    cta_style?: string;
}

@Component({
    selector: 'product-banner-block',
    templateUrl: './product-banner-block.component.html',
    standalone: true,
    imports: [
        ActionComponent,
        CommonModule,
        DfImageDirective,
        HrefComponent,
        ICmsClientModule,
        LocalVimeoPlayerComponent,
        SizeClassDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<ProductBannerBlockComponent>({
    id: 'product-banner-block',
    block: {
        url: 'product-banner-block'
    },
    name: 'Product Banner',
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Layout Variant',
            options: [
                { key: 'image', title: 'Image' },
                { key: 'video', title: 'Video' }
            ],
            default: 'image'
        },
        <NICmsContentField.IField>{
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Background image'
        },
        <NICmsContentField.IField>{
            key: 'video_id',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Vimeo ID or URL',
            canShow: (data: IProductBannerBlockDataContent) => data.variant === 'video'
        },
        <NICmsContentField.IField>{
            key: 'color',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Text Color',
            colorPreview: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'horizontal_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Horizontal content alignment',
            options: HorizontalAlignmentOptions,
            default: 'center'
        },
        <NICmsContentField.IChoiceField>{
            key: 'vertical_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Vertical content alignment',
            options: VerticalAlignmentOptions,
            default: 'middle'
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title'
        },
        <NICmsContentField.IField>{
            key: 'cta_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Button Text'
        },
        <NICmsContentField.IField>{
            key: 'cta_url',
            type: NICmsContentField.EFieldType.text,
            title: 'Button Url'
        },
        <NICmsContentField.IChoiceField>{
            key: 'cta_style',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Style',
            options: ButtonsOptions,
            default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined
        }
    ]
})
export class ProductBannerBlockComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: IProductBannerBlockDataContent;

    image = input.required<string>();
    ratio = input('auto');
    priority = input(false);
    contentChange = output<IProductBannerBlockDataContent>();

    contentChangedDebounced!: () => void;

    private _distroyed = false;

    override ngOnInit(): void {
        super.ngOnInit();

        this.contentChangedDebounced = this.timeoutService.debounce(() => {
            if (!this._distroyed) {
                this.contentChange.emit(this.content);
            }
        }, 10);
    }

    override ngOnDestroy(): void {
        this._distroyed = true;
        super.ngOnDestroy();
    }

    override detectChanges(changes?: NICmsContentComponent.IDetectionChanges): void {
        const watchPath = ['content', 'variant', 'image', 'video_id'];

        if (
            watchPath.some(path => (changes?.['content'] as NICmsContentComponent.Mutation)?.changeEvent?.valuePath?.includes(path)) ||
            watchPath.some(path => !!(changes?.['content'] as NICmsContentComponent.Change)?.currentValue?.[path])
        ) {
            this.contentChangedDebounced();
        } else {
            super.detectChanges();
        }
    }
}
